import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';

const getMessageRound = (message) => {
  const round = Number(message?.meta?.round);
  return Number.isInteger(round) && round > 0 ? round : null;
};

const formatMessageTime = (createdAt) => {
  if (!createdAt) return '';

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

function RoomChatPanel({
  currentUserId,
  event,
  loading = false,
  messages = [],
  onSubmit,
  postCooldown = false,
  posting = false,
}) {
  const [input, setInput] = useState('');
  const disabled = loading || posting || postCooldown;
  const buttonLabel = posting ? '送信中...' : postCooldown ? '少し待ってね' : '送信';
  const isLongButtonLabel = buttonLabel.length > 2;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedInput = input.trim();
    if (!trimmedInput || disabled || typeof onSubmit !== 'function') {
      return;
    }

    const submitted = await onSubmit(trimmedInput);
    if (submitted) {
      setInput('');
    }
  };

  return (
    <Box
      component="section"
      sx={{
        width: 'min(90%, 960px)',
        mt: 3,
        mb: 4,
        p: 2,
        border: '1px solid #ddd',
        borderRadius: 2,
        backgroundColor: '#fff',
      }}
    >
      <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
        チャット
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {loading && messages.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">読み込み中...</Typography>
          </Box>
        ) : null}

        {!loading && messages.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            まだメッセージはありません。
          </Typography>
        ) : null}

        {messages.map((message, index) => {
          const isMine = Number(message.user_id) === Number(currentUserId);
          const isOwnerMessage = event?.owner !== undefined && event?.owner !== null
            ? String(message.user_id) === String(event.owner)
            : false;
          const round = getMessageRound(message);
          const previousRound = getMessageRound(messages[index - 1]);
          const shouldShowRoundDivider = round !== null && round !== previousRound;
          const answerColor = message.meta?.answer_color;
          const hitStreak = Number(message.meta?.hit_streak);
          const messageTime = formatMessageTime(message.created_at);
          const name = isMine ? '自分' : (message.user_name || `ユーザー${message.user_id ?? ''}`);

          return (
            <React.Fragment key={message.id}>
              {shouldShowRoundDivider ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                  <Box sx={{ flex: 1, borderTop: '1px solid #ddd' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ラウンド{round}
                  </Typography>
                  <Box sx={{ flex: 1, borderTop: '1px solid #ddd' }} />
                </Box>
              ) : null}
              <Typography
                variant="body2"
                sx={{
                  px: 0.75,
                  py: 0.4,
                  borderRadius: 1,
                  bgcolor: answerColor ? `${answerColor}1f` : 'transparent',
                  borderLeft: answerColor ? `3px solid ${answerColor}` : 'none',
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {name}
                </Box>
                {isOwnerMessage ? (
                  <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, borderRadius: 1, bgcolor: '#e0f7fa', color: '#007c91', fontSize: '0.72rem', fontWeight: 800 }}>
                    OWNER
                  </Box>
                ) : null}
                : {message.message}
                {Number.isFinite(hitStreak) && hitStreak >= 2 ? (
                  <Chip
                    component="span"
                    label={`${hitStreak}連続的中`}
                    size="small"
                    color="success"
                    sx={{ ml: 0.75, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.72rem', fontWeight: 800 } }}
                  />
                ) : null}
                {messageTime ? (
                  <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {messageTime}
                  </Box>
                ) : null}
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1 }}>
        <TextField
          disabled={disabled}
          fullWidth
          onChange={(event) => setInput(event.target.value)}
          placeholder="メッセージを入力"
          size="small"
          value={input}
        />
        <Button
          disabled={disabled || input.trim() === ''}
          type="submit"
          variant="contained"
          sx={{
            flex: '0 0 104px',
            width: 104,
            minWidth: 104,
            height: 40,
            px: 1,
            fontSize: isLongButtonLabel ? '0.78rem' : '0.875rem',
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {buttonLabel}
        </Button>
      </Box>
    </Box>
  );
}

export default RoomChatPanel;
